import '../index.less';

import { Button, Popover, Table, Tag } from 'antd';

import InfoGrid from '../../../components/InfoGrid';
import { numberWithCommas } from '../../../helpers/number';
import { displayDate } from '../../../utils/datetime.util';
import { SME_ORGANIZATION_TYPE } from '../../../enum/smeApplication';
import { getLoanTypeByOrganizationType } from '../../../utils/string.util';
import { APPLICATION_TYPE } from '../../../enum/applicationType';
import { AccountEnquiryResponse } from '../../../features/collection/collectionSlice';

type AccountDetailsProps = {
  accountEnquiry: AccountEnquiryResponse;
};

const AccountDetails: React.FC<AccountDetailsProps> = (props) => {
  const { accountEnquiry } = props;

  const accountDetails = accountEnquiry?.accountDetails;
  const agreementDetails = accountEnquiry?.agreementDetails;

  const accountDetailsInfo1 = [
    {
      title: 'Agreement No',
      value: accountDetails?.agreementNo,
    },
    {
      title: 'Agreement Date',
      value: accountDetails?.agreementSignDate
        ? displayDate(new Date(accountDetails?.agreementSignDate))
        : agreementDetails?.signDate
        ? displayDate(new Date(agreementDetails?.signDate))
        : '-',
    },
    {
      title: 'Financing Type',
      value:
        accountDetails?.organizationType === APPLICATION_TYPE.ISLAMIC ? 'ISLAMIC' : 'CONVENTIONAL',
    },
    {
      title: 'Loan Type',
      value: getLoanTypeByOrganizationType(accountDetails?.organizationType),
    },
    {
      title: 'Disbursement Date',
      value: accountDetails?.disbursementPostedDate
        ? displayDate(accountDetails?.disbursementPostedDate)
        : '-',
    },
    {
      title: 'Disbursement Amount',
      value:
        accountDetails?.disbursementAmount != null
          ? `RM ${numberWithCommas(accountDetails?.disbursementAmount)}`
          : '-',
    },
    {
      title: 'First Instalment Date',
      value:
        accountDetails?.agreementSignDate && accountDetails?.firstDueDate
          ? displayDate(accountDetails?.firstDueDate)
          : '-',
    },
    {
      title: 'Maturity Date',
      value: accountDetails?.maturedDate ? displayDate(accountDetails?.maturedDate) : '-',
    },
    {
      title: 'Finance Amount',
      value:
        accountDetails?.totalFinanceAmount != null
          ? `RM ${numberWithCommas(accountDetails?.totalFinanceAmount)}`
          : '-',
    },
    {
      title: 'Interest Amount',
      value:
        accountDetails?.totalInterestAmount != null
          ? `RM ${numberWithCommas(accountDetails?.totalInterestAmount)}`
          : '-',
    },
    {
      title: 'Interest Rate',
      value:
        accountDetails?.loanInterestRate != null
          ? `${numberWithCommas(accountDetails?.loanInterestRate)} %`
          : '-',
    },
    {
      title: 'Instalment Period',
      value: accountDetails?.totalInstalmentMonth ?? '-',
    },
    {
      title: 'Total Instalment Amount',
      value:
        accountDetails?.totalPayableAmount != null ? (
          <Popover
            className="p-0"
            content={
              <Table
                className="max-h-96 overflow-auto"
                size="small"
                dataSource={accountEnquiry?.totalTenure ?? []}
                pagination={false}
                columns={[
                  {
                    title: 'Due Date',
                    dataIndex: 'dueDate',
                    key: 'dueDate',
                    align: 'center',
                    render: (text) => (text != null ? `${displayDate(text)}` : '-'),
                  },
                  {
                    title: 'Instalment Amount',
                    dataIndex: 'instalmentAmount',
                    key: 'instalmentAmount',
                    render: (text: string | number | null | undefined) =>
                      text != null ? `RM ${numberWithCommas(text)}` : '-',
                    align: 'center',
                  },
                ]}
              />
            }
          >
            <Button type="link">
              {`RM ${numberWithCommas(accountDetails?.totalPayableAmount)}`}
            </Button>
          </Popover>
        ) : (
          '-'
        ),
    },
    {
      title: 'Monthly Instalment Amount',
      value:
        accountDetails?.monthlyInstalmentAmount != null
          ? `RM ${numberWithCommas(accountDetails?.monthlyInstalmentAmount)}`
          : '-',
    },
    {
      title: 'Final Instalment Amount',
      value:
        accountDetails?.lastInstalmentAmount != null
          ? `RM ${numberWithCommas(accountDetails?.lastInstalmentAmount)}`
          : '-',
    },
    {
      title: 'Payment Method',
      value: accountEnquiry?.accountDetails?.paymentMethod ?? '-',
    },
  ];

  const accountDetailsInfo2 = [
    {
      title: 'Processing Fee',
      value:
        accountDetails?.adminFee != null
          ? `RM ${numberWithCommas(accountDetails?.adminFee)}`
          : agreementDetails?.application?.productProcessingFee != null
          ? `RM ${numberWithCommas(agreementDetails?.application?.productProcessingFee)}`
          : '-',
    },
    {
      title: 'Stamp Duty Fee',
      value:
        accountDetails?.stampingDutyFee != null
          ? `RM ${numberWithCommas(accountDetails?.stampingDutyFee)}`
          : '-',
    },
    {
      title: 'Wakalah Fee',
      value:
        accountDetails?.wakalahFee != null
          ? `RM ${numberWithCommas(accountDetails?.wakalahFee)}`
          : '-',
    },
    {
      title: 'Security Type',
      value: agreementDetails?.application?.loanType || '-',
    },
  ];

  const accountDetailsInfo3 = [
    {
      title: 'Campaign Code',
      value: agreementDetails?.application?.productReferralCode ?? '-',
    },
    {
      title: 'Discount Amount',
      value: agreementDetails?.application?.productProcessingFeeToDiscount
        ? `RM ${numberWithCommas(agreementDetails?.application?.productProcessingFeeToDiscount)}`
        : '-',
    },
    ...(accountDetails?.organizationType !== SME_ORGANIZATION_TYPE.STAFF
      ? [
          {
            title: 'Partner ID No',
            value:
              agreementDetails?.application?.user?.partnerId ??
              accountDetails?.applicantPartnerId ??
              '-',
          },
        ]
      : []),
    ...(accountDetails?.smeApplication // Only for SME
      ? [
          {
            title: 'Organization Type',
            value: accountDetails?.organizationType ?? '-',
          },
        ]
      : []),
  ];

  const accountDetailsInfo4 = [
    {
      title: 'Policy Status',
      value: agreementDetails?.policyStatus ?? '-',
    },
    {
      title: 'Policy Number',
      value: agreementDetails?.policyNumber ?? '-',
    },
    {
      title: 'Policy Insurance Date',
      value: agreementDetails?.policyIssuanceDate ? agreementDetails.policyIssuanceDate : '-',
    },
    {
      title: 'Loan Insurance Amount',
      value: agreementDetails?.application?.loanTotalPremium
        ? `RM ${numberWithCommas(agreementDetails?.application?.loanTotalPremium)}`
        : '-',
    },
  ];

  const accountDetailsInfo5 = [
    {
      title: 'Old Agreement No',
      value: accountDetails?.smeApplication?.oldAgreementNo ?? '-',
    },

    {
      title: 'Staff ID',
      value: accountDetails?.smeApplication?.staffId ?? '-',
    },
  ];

  return (
    <div className="account-enquiry-container">
      <div className="account-enquiry-section-container">
        <div className="flex items-center mb-3">
          <h3 className="account-enquiry-section-title mb-0 mr-2">Account Details</h3>
          {<Tag>{accountDetails?.status}</Tag>}
        </div>
        <div className="info-container">
          <InfoGrid data={accountDetailsInfo1} />
          <div className="p-4" />
          <InfoGrid data={accountDetailsInfo2} />
          <div className="p-4" />
          <InfoGrid data={accountDetailsInfo3} />
          <div className="p-4" />
          {!accountDetails?.smeApplicationId ? (
            <InfoGrid data={accountDetailsInfo4} />
          ) : (
            <InfoGrid data={accountDetailsInfo5} />
          )}
        </div>
      </div>
    </div>
  );
};

export default AccountDetails;
